import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowRight, 
  Star, 
  Clock, 
  Shield, 
  Award, 
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/testimonials`)
        ]);
        setServices(servicesRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="https://images.unsplash.com/photo-1586333109867-812586269a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzdGVlbCUyMGZhYnJpY2F0aW9uJTIwd2VsZGluZyUyMHNwYXJrcyUyMGluZHVzdHJpYWwlMjBmYWN0b3J5fGVufDB8fHx8MTc3MDA2MDIwNnww&ixlib=rb-4.1.0&q=85"
            className="w-full h-full object-cover"
            webkit-playsinline="true"
          >
            <source 
              src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/0ng5wuah_Nassau%20Engineering%20Video%201.mp4" 
              type="video/mp4" 
            />
            Your browser does not support the video tag.
          </video>
          {/* Dark overlay gradient for text readability */}
          <div className="absolute inset-0 bg-slate-950/50" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 via-transparent to-slate-950" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 text-center">
          <div className="animate-slide-up">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              We Specialize in Fabrication
            </p>
            <h1 
              className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight mb-6 text-shadow"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Metal, Steel Fabrication,
              <br />
              <span className="text-blue-500">and Engineering</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              Nassau Engineering stands unparalleled in the Irish market, delivering precision 
              engineering solutions for Pharmaceutical, Construction, and Architecture sectors.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/services">
                <Button 
                  data-testid="hero-view-services-btn"
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider btn-glow px-8"
                >
                  View Our Services
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  data-testid="hero-free-estimate-btn"
                  size="lg"
                  variant="outline"
                  className="border-slate-600 hover:border-blue-500 hover:text-blue-400 rounded-sm uppercase tracking-wider px-8"
                >
                  Free Estimate
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 animate-slide-up animation-delay-200">
            {[
              { value: "25+", label: "Years Experience" },
              { value: "500+", label: "Projects Completed" },
              { value: "100+", label: "Happy Clients" },
              { value: "5.0", label: "Star Rating" },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-blue-500" style={{ fontFamily: 'var(--font-heading)' }}>
                  {stat.value}
                </p>
                <p className="text-slate-400 text-sm uppercase tracking-wider mt-1">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-500 rounded-full flex items-start justify-center p-1">
            <div className="w-1.5 h-3 bg-blue-500 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Free Estimate Banner */}
      <section data-testid="estimate-banner" className="bg-blue-600 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <Clock className="w-8 h-8 text-white" />
              <div>
                <p className="text-white font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>
                  Free Fabrication Estimate Within 24 Hours
                </p>
                <p className="text-blue-100 text-sm">
                  Depending on the size of the job
                </p>
              </div>
            </div>
            <Link to="/contact">
              <Button 
                data-testid="estimate-cta-btn"
                className="bg-white text-blue-600 hover:bg-slate-100 rounded-sm uppercase tracking-wider"
              >
                Request Estimate
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section data-testid="about-preview" className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
                About Nassau Engineering
              </p>
              <h2 
                className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Exploring the World of
                <span className="text-blue-500"> Steel Fabrication</span>
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Whether you're seeking to design, manufacture, or install metal products, 
                or in search of high-quality fabrication services, Nassau Engineering is 
                your trusted partner for navigating the diverse landscape of engineering 
                and metalwork.
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                At Nassau, we possess expertise in various types of steel and metal 
                fabrication, and have a deep understanding of how these materials play 
                a crucial role in various industries.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Shield, text: "Quality Assured" },
                  { icon: Award, text: "Industry Certified" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-600/20 rounded-sm flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-blue-500" />
                    </div>
                    <span className="text-slate-300 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              <Link to="/about">
                <Button 
                  data-testid="about-learn-more-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider"
                >
                  Learn More About Us
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative pb-6 pl-6">
              <img
                src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/h867hbts_smoking%20booth%20067.jpg"
                alt="Steel fabrication work"
                className="rounded-sm w-full h-[400px] object-cover object-center"
              />
              <div className="absolute bottom-0 left-0 bg-blue-600 p-6 rounded-sm">
                <p className="text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>25+</p>
                <p className="text-blue-100 text-sm uppercase tracking-wider">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Our Services
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Metal Fabrication <span className="text-blue-500">Solutions</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              "We focus on the problem, the question and the solution. And we always think ahead."
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <Card 
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="bg-slate-950 border-slate-800 rounded-sm card-hover overflow-hidden group"
              >
                <div className="service-image h-48 overflow-hidden">
                  <img
                    src={service.image_url}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 
                    className="text-lg font-bold text-white uppercase tracking-tight mb-3 group-hover:text-blue-400 transition-colors"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                    {service.description}
                  </p>
                  <Link 
                    to="/services"
                    className="text-blue-400 text-sm uppercase tracking-wider flex items-center hover:text-blue-300 transition-colors"
                  >
                    Learn More
                    <ChevronRight className="ml-1 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                data-testid="services-view-all-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider btn-glow"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section data-testid="clients-section" className="py-20 bg-slate-950 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              We Have a Huge Portfolio of Work
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Working With the Best <span className="text-blue-500">Clients and Partners</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 px-8 sm:px-0">
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 flex items-center justify-center hover:scale-105 hover:shadow-xl transition-all duration-300 aspect-[4/2] sm:aspect-square overflow-hidden max-w-[75%] sm:max-w-full mx-auto w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/chuhidor_Jones%20png%20%281%29.png" 
                alt="Jones Engineering" 
                className="w-3/4 h-3/4 sm:w-full sm:h-full object-contain"
              />
            </div>
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 flex items-center justify-center hover:scale-105 hover:shadow-xl transition-all duration-300 aspect-[4/2] sm:aspect-square max-w-[75%] sm:max-w-full mx-auto w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/hghoayk1_amgen.png" 
                alt="Amgen" 
                className="w-3/4 h-3/4 sm:w-full sm:h-full object-contain"
              />
            </div>
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 flex items-center justify-center hover:scale-105 hover:shadow-xl transition-all duration-300 aspect-[4/2] sm:aspect-square overflow-hidden max-w-[75%] sm:max-w-full mx-auto w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/gy1dr5ag_daa-logo.png" 
                alt="Dublin Airport Authority" 
                className="w-3/4 h-3/4 sm:w-full sm:h-full object-contain sm:scale-150"
              />
            </div>
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 flex items-center justify-center hover:scale-105 hover:shadow-xl transition-all duration-300 aspect-[4/2] sm:aspect-square max-w-[75%] sm:max-w-full mx-auto w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/vwm6uplq_amazon.png" 
                alt="Amazon" 
                className="w-3/4 h-3/4 sm:w-full sm:h-full object-contain"
              />
            </div>
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 flex items-center justify-center hover:scale-105 hover:shadow-xl transition-all duration-300 aspect-[4/2] sm:aspect-square max-w-[75%] sm:max-w-full mx-auto w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/9s19cnuk_Pfizer_logo.svg" 
                alt="Pfizer" 
                className="w-3/4 h-3/4 sm:w-full sm:h-full object-contain"
              />
            </div>
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 flex items-center justify-center hover:scale-105 hover:shadow-xl transition-all duration-300 aspect-[4/2] sm:aspect-square max-w-[75%] sm:max-w-full mx-auto w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/k1f4advy_conneely-building-civil-logo.webp" 
                alt="Conneely Building & Civil" 
                className="w-3/4 h-3/4 sm:w-full sm:h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Testimonials
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              What Our <span className="text-blue-500">Clients Say</span>
            </h2>
            <div className="flex items-center justify-center gap-1 mt-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="ml-2 text-white font-bold">5.0</span>
              <span className="text-slate-400 ml-1">Based on 4 reviews</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id}
                data-testid={`testimonial-card-${testimonial.id}`}
                className="bg-slate-950 border-slate-800 rounded-sm p-6"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-400 text-sm mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-slate-500 text-sm">{testimonial.company}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1586333109867-812586269a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzdGVlbCUyMGZhYnJpY2F0aW9uJTIwd2VsZGluZyUyMHNwYXJrcyUyMGluZHVzdHJpYWwlMjBmYWN0b3J5fGVufDB8fHx8MTc3MDA2MDIwNnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Ready to Start Your <span className="text-blue-500">Project?</span>
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Contact our team today to discuss your steel fabrication and engineering needs. 
            We offer free estimates within 24 hours.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="cta-contact-btn"
              size="lg"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider btn-glow px-8"
            >
              Contact Us Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
